import React, { useEffect, useState } from "react";
import {
  Box,
  Fab,
  List,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ProfessionalAffiliation from "../../../../../types/ProfessionalAffiliation";
import { sortProjectsByDate } from "../../../../utils/helpers";

interface WorkExperienceSidebarProps {
  affiliations: ProfessionalAffiliation[];
  visible: boolean;
  onToggle: () => void;
}

const AFF_HEADER_PREFIX = "affiliation-";
const AFF_HEADER_SUFFIX = "-header";
const PROJECT_PREFIX = "affiliation-";

export const WorkExperienceSidebar: React.FC<WorkExperienceSidebarProps> = ({
  affiliations,
  visible,
  onToggle,
}) => {
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    const ids: string[] = [];
    affiliations.forEach((aff, i) => {
      ids.push(`${AFF_HEADER_PREFIX}${i}${AFF_HEADER_SUFFIX}`);
      aff.projects.forEach((_, pj) => {
        ids.push(`${PROJECT_PREFIX}${i}-project-${pj}`);
      });
    });

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries.filter((e) => e.isIntersecting);
        if (visibleEntries.length === 0) return;
        const sorted = visibleEntries.sort(
          (a, b) =>
            (a.boundingClientRect?.top ?? 0) - (b.boundingClientRect?.top ?? 0)
        );
        const top = sorted[0];
        if (top?.target?.id) setActiveId(top.target.id);
      },
      {
        rootMargin: "-80px 0px -60% 0px",
        threshold: 0,
      }
    );

    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [affiliations]);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      {!visible && (
        <Tooltip title="Show outlines" placement="right">
          <Fab
            size="small"
            onClick={onToggle}
            aria-label="Show outlines"
            sx={(theme) => ({
              position: "fixed",
              bottom: 88,
              left: 24,
              zIndex: 1099,
              backgroundColor: theme.palette.grey[800],
              color: theme.palette.grey[50],
              boxShadow: 0,
              "&:hover": { backgroundColor: theme.palette.grey[700] },
            })}
          >
            <MenuIcon fontSize="small" />
          </Fab>
        </Tooltip>
      )}
      {visible && (
    <Box
      sx={{
        position: "fixed",
        left: 0,
        top: "calc(var(--template-frame-height, 0px) + 28px + 56px)",
        width: 220,
        height: "calc(100vh - var(--template-frame-height, 0px) - 28px - 56px - 88px)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        pr: 1,
        backgroundColor: "background.default",
        zIndex: 100,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", mb: 1, pl: 1, flexShrink: 0 }}>
        <Typography variant="overline" color="text.secondary">
          Outline
        </Typography>
      </Box>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overscrollBehavior: "contain",
          }}
        >
        <List dense sx={{ py: 0 }}>
          {affiliations.map((aff, i) => (
            <React.Fragment key={i}>
              <ListItemButton
                onClick={() =>
                  scrollTo(`${AFF_HEADER_PREFIX}${i}${AFF_HEADER_SUFFIX}`)
                }
                selected={
                  activeId === `${AFF_HEADER_PREFIX}${i}${AFF_HEADER_SUFFIX}`
                }
                sx={{ py: 0.25, pl: 1 }}
              >
                <ListItemText
                  primary={
                    aff.employer?.trim()
                      ? aff.employer
                      : `Employer ${i + 1}`
                  }
                  primaryTypographyProps={{
                    variant: "body2",
                    fontWeight:
                      activeId === `${AFF_HEADER_PREFIX}${i}${AFF_HEADER_SUFFIX}`
                        ? 600
                        : 400,
                  }}
                />
              </ListItemButton>
              {sortProjectsByDate(aff.projects).map((project) => {
                const pj = aff.projects.indexOf(project);
                return (
                <ListItemButton
                  key={pj}
                  onClick={() =>
                    scrollTo(`${PROJECT_PREFIX}${i}-project-${pj}`)
                  }
                  selected={
                    activeId === `${PROJECT_PREFIX}${i}-project-${pj}`
                  }
                  sx={{ py: 0.25, pl: 2.5 }}
                >
                  <ListItemText
                    primary={
                      project.client?.trim()
                        ? project.client
                        : `Project ${pj + 1}`
                    }
                    primaryTypographyProps={{
                      variant: "body2",
                      fontSize: "0.8125rem",
                      fontWeight:
                        activeId === `${PROJECT_PREFIX}${i}-project-${pj}`
                          ? 600
                          : 400,
                    }}
                  />
                </ListItemButton>
                );
              })}
            </React.Fragment>
          ))}
        </List>
        </Box>
      <Box sx={{ flexShrink: 0, p: 1, display: "flex", justifyContent: "flex-end" }}>
        <Tooltip title="Collapse" placement="right">
          <Fab
            size="small"
            onClick={onToggle}
            aria-label="Collapse outline"
            sx={(theme) => ({
              backgroundColor: theme.palette.grey[800],
              color: theme.palette.grey[50],
              boxShadow: 0,
              "&:hover": { backgroundColor: theme.palette.grey[700] },
            })}
          >
            <ChevronLeftIcon fontSize="small" />
          </Fab>
        </Tooltip>
      </Box>
    </Box>
      )}
    </>
  );
};
